import TurndownService from "turndown";
import MarkdownIt from "markdown-it";
import {
  story as RpcStory,
  query_recursive_types as RpcRecursiveTypes,
  query_types as RpcQueryTypes,
} from "../infra/api/rpc/api";
import { validateURL } from "../utils/utils";
import { PublishRange, publishRangeFromRpcSharedWithStatus } from "./novel";
import type { Genre } from "./genre";
import { rpcGenreToGenre } from "./genre";

const RE_MARKDOWN_IMAGE = /!\[(.*?)\]\((.*?)\)/gim;

/**
 * 全角スペースの一時的なプレースホルダー
 * Quillのclipboard処理で空白文字が削除されるのを防ぐため、
 * プライベート使用エリア（PUA）の文字に置換する
 *
 * Quillのclipboard.tsでは /[^\S\u00a0]/g で非NBSP空白を通常スペースに変換し、
 * その後 /^ / で先頭スペースを削除する処理がある。
 * 全角スペース（\u3000）は \s にマッチするため削除されてしまう。
 * PUA文字（\uE000）は空白文字ではないため、この処理を回避できる。
 */
export const FULLWIDTH_SPACE_PLACEHOLDER = "\uE000";

/**
 * タブ文字の一時的なプレースホルダー
 * Quillのclipboard処理でタブ文字が通常スペースに変換されるのを防ぐため、
 * プライベート使用エリア（PUA）の文字に置換する
 */
export const TAB_PLACEHOLDER = "\uE001";

/**
 * Quill Delta内のPUAプレースホルダーを元の文字に復元する
 *
 * Quillのclipboard.convert後のDeltaに含まれるPUA文字を元の文字に戻す。
 * - \uE000 → \u3000 (全角スペース)
 * - \uE001 → \t (タブ)
 *
 * @param ops - QuillのDelta操作の配列
 * @returns PUAプレースホルダーが復元されたDelta操作の配列
 */
export const restoreFullWidthSpacesInDelta = <
  T extends { insert?: string | unknown },
>(
  ops: T[],
): T[] => {
  const fullWidthSpaceRegex = new RegExp(FULLWIDTH_SPACE_PLACEHOLDER, "g");
  const tabRegex = new RegExp(TAB_PLACEHOLDER, "g");

  return ops.map((op) => {
    if (typeof op.insert === "string") {
      return {
        ...op,
        insert: op.insert
          .replace(fullWidthSpaceRegex, "\u3000")
          .replace(tabRegex, "\t"),
      };
    }
    return op;
  });
};

export type Episode = {
  userId: string;
  tags?: string[];
  id?: string;
  novelId?: string;
  novelTitle?: string;
  novelIndex?: number;
  novelPublishFor: PublishRange | null;
  novelGenre: Genre | null;
  title: string;
  description?: string;
  thumbnailId?: string;
  thumbnailUrl?: string;
  sensitiveFlag?: RpcQueryTypes.SensitiveFlag;
  status?: RpcStory.StoryStatus;
  novelScript?: string;
  chatScript?: RpcRecursiveTypes.IStoryChatNovelScriptResponse | null;
  isOfficial: boolean;
  scriptType: RpcQueryTypes.StoryScriptType;
  // **一度でも**公開したことがあるか
  isPublished: boolean;
};

export const episodeFromRpcStory = (
  rpcStory: RpcRecursiveTypes.IStoryResponse,
): Episode => {
  const tags =
    rpcStory.tags && rpcStory.tags.length > 0
      ? rpcStory.tags
      : rpcStory.series?.tags;
  const ep: Episode = {
    userId: rpcStory.user?.id?.value || "",
    tags: tags?.map((c) => c.value || "") || [],
    id: rpcStory.id?.value || "",
    novelId: rpcStory.series?.id?.value || "",
    novelTitle: rpcStory.series?.title?.value || "",
    novelIndex: rpcStory.seriesIndex?.value || undefined,
    novelPublishFor: rpcStory.series?.userAvailability
      ? publishRangeFromRpcSharedWithStatus(rpcStory.series?.sharedWithStatus)
      : null,
    novelGenre: rpcGenreToGenre(rpcStory.series?.genre || null),

    title: rpcStory.title?.value || "",
    description: "",
    thumbnailId:
      rpcStory.thumbnail?.id?.value ||
      rpcStory.series?.thumbnail?.id?.value ||
      "",
    thumbnailUrl: rpcStory.thumbnail?.servingUrl?.value || "",
    sensitiveFlag:
      rpcStory.sensitiveFlag ||
      RpcQueryTypes.SensitiveFlag.SENSITIVE_FLAG_UNSPECIFIED,
    status: rpcStory.status || RpcStory.StoryStatus.DRAFT,
    isOfficial: rpcStory.isOfficial?.value || false,
    scriptType:
      rpcStory.scriptType ||
      RpcQueryTypes.StoryScriptType.STORY_SCRIPT_TYPE_CHAT_NOVEL,
    isPublished: Number(rpcStory.publishedAt?.seconds ?? 0) > 0,
  };
  if (ep.scriptType === RpcQueryTypes.StoryScriptType.STORY_SCRIPT_TYPE_NOVEL) {
    ep.novelScript = rpcStory.novelScript?.fullScript?.value || "";
  } else {
    ep.chatScript = rpcStory.chatNovelScript;
  }
  return ep;
};

export const copyRpcSeriesDataToEpisode = (
  series: RpcRecursiveTypes.ISeriesResponse,
  episode: Episode,
): Episode => ({
  ...episode,
  thumbnailId: series.thumbnail?.id?.value || "",
  tags: series.tags?.map((c) => c.value || "") || [],
  // NOTE: STU-140 Episode description not set
  description: "",
  novelId: series.id?.value || "",
});

export const toMarkdown = (htmlScript: string): string => {
  const sv = new TurndownService({
    headingStyle: "atx",
    emDelimiter: "*",
    hr: "---",
  });

  // Quill adds always paragraphs to any new line
  // and they get converted as multiple new lines in markdown
  // so before sending to server, just strip all these not needed lines
  // And also add a generic alt attribute to images for now

  // Browser's innerHTML encodes tab characters as &#9;, convert them back
  const withDecodedTabs = htmlScript.replaceAll("&#9;", "\t");

  // Preserve leading whitespace: convert spaces after > to non-breaking spaces
  // This prevents TurndownService from trimming them
  // Note: \s doesn't include full-width space (\u3000) in JavaScript, so we add it explicitly
  const withPreservedSpaces = withDecodedTabs.replace(
    />([\s\u3000]+)([^<])/g,
    (_, spaces: string, nextChar: string) => {
      const preserved = spaces
        .replace(/ /g, "\u00A0") // half-width space → non-breaking space
        .replace(/\t/g, TAB_PLACEHOLDER) // tab → PUA placeholder (TurndownService removes tabs)
        .replace(/\u3000/g, FULLWIDTH_SPACE_PLACEHOLDER); // full-width space → PUA placeholder
      return `>${preserved}${nextChar}`;
    },
  );

  const curated = withPreservedSpaces
    .replaceAll("<p><br></p>", "$br$") // mark additional empty lines as $br$
    .replaceAll("<p>", "") // remove <p> tags
    .replaceAll("</p>", "$endp$") // mark end of paragraph with $endp$ token
    .replaceAll("<img alt='画像'", "<img")
    .replaceAll("<img", "<img alt='画像'") // add a generic alt attribute for images
    .replaceAll('">', '">\n'); // also ensure there is nothing after image in the same line

  // Turndownservice converts from html to markdown, but it adds some extra blank lines
  // on HT element, just override these here.
  // And also underline for us will be stored as italic in server between `*`
  const md = sv
    .addRule("underline", {
      filter: "u",
      replacement: (content) => `<u>${content}</u>`,
    })
    .turndown(curated);

  // after conversion, put back our special cases:
  const withBr = md
    .replaceAll("$br$", "<br>\n") // each empty line is stored as <br> and actual line break
    .replaceAll("$endp$", "\n\n") // each paragraph end is stored with two line breaks
    .replace(/\u00A0/g, " ") // convert non-breaking spaces back to regular spaces
    .replace(/\uFEFF/g, "") // remove zero-width no-break spaces (legacy, kept for compatibility)
    .replace(new RegExp(FULLWIDTH_SPACE_PLACEHOLDER, "g"), "\u3000") // restore PUA placeholders to full-width spaces
    .replace(new RegExp(TAB_PLACEHOLDER, "g"), "\u3000\u3000"); // タブを全角スペース2つに変換（日本語組版に合わせる）
  return withBr;
};

export type FromMarkdownOptions = {
  /**
   * Quillエディタ用の出力かどうか
   * true: PUA文字を使用してQuillのclipboard処理を回避する
   * false: 通常の文字を使用する（プレビュー表示用）
   * @default true
   */
  forQuill?: boolean;
};

export const fromMarkdown = (
  markdownScript: string,
  options: FromMarkdownOptions = {},
): string => {
  const { forQuill = true } = options;
  const md = MarkdownIt();
  md.set({ html: true, xhtmlOut: true });

  // If we convert directly from server markdown to HTML, the result doesn't look like
  // the original in quill editor, so I add some adjustments so it looks exactly like it was
  // edited
  // For example, blank lines are ignored, but I convert them to <p><br/></p> which is what quill expects
  const sanitized: string[] = [];

  // Helper function to convert whitespace characters based on forQuill option
  const convertWhitespace = (char: string): string => {
    if (char === "\t") {
      // Tab: PUA placeholder for Quill, or 2 full-width spaces for preview (日本語組版に合わせる)
      return forQuill ? TAB_PLACEHOLDER : "\u3000\u3000";
    }
    if (char === " ") {
      // Half-width space: always use NBSP to prevent HTML collapsing
      return "\u00A0";
    }
    if (char === "\u3000") {
      // Full-width space: PUA for Quill to prevent trimming, otherwise keep as-is
      return forQuill ? FULLWIDTH_SPACE_PLACEHOLDER : "\u3000";
    }
    return char;
  };

  markdownScript.split("\n").forEach((line) => {
    // Use custom trim that handles full-width spaces
    const trimmed = line.replace(/^[\s\u3000]+|[\s\u3000]+$/g, "");
    if (trimmed === "") {
      return;
    }

    // Extract leading whitespace (including full-width spaces and tabs)
    const leadingWhitespace = line.match(/^([\s\u3000]+)/)?.[1] || "";
    // Convert leading whitespace to preserved characters
    // This ensures both MarkdownIt and Quill preserve them
    // - half-width space: convert to non-breaking space (\u00A0)
    // - tab (\t): PUA placeholder for Quill, keep as-is for preview
    // - full-width space (\u3000): PUA placeholder for Quill, keep as-is for preview
    const preservedWhitespace = leadingWhitespace
      .split("")
      .map(convertWhitespace)
      .join("");

    // Title and image cases, do not add <br/>
    if (trimmed === "<br>") {
      sanitized.push("<p><br></p>");
      return;
    }
    // End of lines are added but only for "normal" texts, no for titles or images
    if (trimmed.startsWith("# ")) {
      // For headings, MarkdownIt trims whitespace inside headings, so we need to
      // generate HTML directly instead of using MarkdownIt
      const headingContent = trimmed.slice(2); // Remove "# "
      const headingLeadingWs =
        headingContent.match(/^([\s\u3000]+)/)?.[1] || "";
      const headingText = headingContent.replace(/^[\s\u3000]+/, "");
      // Convert whitespace to preserved characters using the same helper
      const preservedHeadingWs = headingLeadingWs
        .split("")
        .map(convertWhitespace)
        .join("");

      // Process the heading text through MarkdownIt for inline formatting (bold, italic, etc.)
      const tempMd = MarkdownIt();
      tempMd.set({ html: true, xhtmlOut: true });
      // renderInline doesn't wrap in <p> tags
      const renderedText = tempMd.renderInline(headingText);

      // Generate <h1> tag directly with preserved whitespace
      sanitized.push(
        `<h1>${preservedWhitespace}${preservedHeadingWs}${renderedText}</h1>`,
      );
      return;
    }
    // Image, ignore not valid urls
    if (trimmed.startsWith("![")) {
      try {
        const elements = RE_MARKDOWN_IMAGE.exec(trimmed);
        if (elements) {
          const imageUrl = elements[2];
          if (!validateURL(imageUrl)) {
            return;
          }
        }
      } catch (_) {
        // no op
      }
    }

    if (trimmed.startsWith("![") || trimmed === "---") {
      sanitized.push(`\n${line}\n`);
    } else if (preservedWhitespace) {
      // If there's leading whitespace, render the line manually to preserve it
      // MarkdownIt would trim leading whitespace, so we need to handle it ourselves
      const tempMd = MarkdownIt();
      tempMd.set({ html: true, xhtmlOut: true });
      // Render the trimmed content through MarkdownIt for inline formatting
      const renderedContent = tempMd.renderInline(trimmed);
      // Wrap in <p> tag with preserved whitespace
      sanitized.push(`<p>${preservedWhitespace}${renderedContent}</p>`);
    } else {
      // No leading whitespace, let MarkdownIt handle it normally
      sanitized.push(`\n${trimmed}\n`);
    }
  });
  const withBr = sanitized.join("\n");
  const html = md.render(withBr);

  return html;
};
